'use client';

import { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_ALL_JOURNALS } from '@/queries/queries';
import { AnimatePresence } from 'framer-motion';
import Image from 'next/image';
import Tag from '@/components/ui/Tag';
import JournalModal from '@/components/common/journalDetail';

type Journal = {
  id: string;
  name: string;
  awards_text?: string;
  date?: string;
  button: {
    button_text: string;
    button_url: string;
  };
  media?: {
    desktop_media?: { url: string }[];
  };
  description?: { children?: { text: string }[] }[];
};

export default function JournalsData() {
  const { data, loading, error } = useQuery(GET_ALL_JOURNALS);
  const [selectedJournal, setSelectedJournal] = useState<Journal | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const journals: Journal[] = data?.journals || [];

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedJournal(null);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  if (loading) return <p className="p-6 text-[#2a3329]">Loading journals...</p>;
  if (error) return <p className="p-6 text-red-600">Error loading journals.</p>;

  return (
    <main className="min-h-screen bg-[#B6C380] relative overflow-hidden journals px-4 pt-20 md:p-5 w-full m-auto">
      <div className="text-start md:text-center leading-none font-light md:pt-1 md:ml-[9.4rem] xl:ml-[16.5rem]">
        <h1>Journal & News</h1>
      </div>
      <section className="pt-16 md:pt-[8.625rem] pb-20 md:pb-[8.28rem]">
        <div className="mx-auto px-0 2xl:px-36 w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-[64px] md:gap-y-[116px] sm:gap-x-6 md:gap-x-10 xl:gap-x-40 w-full">
            {journals.map((article, index) => (
              <div key={article.id || index} className="project-box">
                <div className="flex justify-between items-center mb-3 leading-none font-dm-mono">
                  <Tag className="text-[#2a3329]">{article?.awards_text}</Tag>
                  <Tag className="text-[#2a3329]">{article.date}</Tag>
                </div>

                <div
                  className="mb-4 relative img-box bg-[#B6C380] !filter-none cursor-pointer transition-all"
                  onMouseMove={handleMouseMove}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onClick={() => setSelectedJournal(article)}
                  style={{ width: '100%', maxWidth: '100%' }}
                >
                  <Image
                    src={article.media?.desktop_media?.[0]?.url || '/images/fallback.jpg'}
                    alt="Journal article"
                    fill
                    className="object-cover transition-all"
                    sizes="(max-width: 768px) 100vw, 33vw"
                  />
                  {hoveredIndex === index && article.button?.button_text && (
                    <button
                      onClick={() => setSelectedJournal(article)}
                      rel="noopener noreferrer"
                      className="text-[#E5E1D5] text-nowrap text-[12px] uppercase font-dm-mono absolute tracking-widest transition-opacity duration-300 flex items-center pointer-events-auto z-10"
                      style={{
                        left: `${mousePos.x}px`,
                        top: `${mousePos.y}px`,
                        transform: 'translate(-50%, -50%)',
                      }}
                    >
                      {article.button.button_text}
                      <Image
                        src="/images/img_frame_659.svg"
                        alt="Arrow"
                        width={12}
                        height={10}
                        className="ml-1"
                      />
                    </button>
                  )}
                </div>

                <p className="text-lg md:text-xl font-light text-[#2a3329] leading-[24px]">
                  {article.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <AnimatePresence>
        {selectedJournal && (
          <JournalModal journal={selectedJournal} onClose={() => setSelectedJournal(null)} />
        )}
      </AnimatePresence>
    </main>
  );
}
